import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X, Loader } from 'react-feather';
import './NotificationToast.css';

export type NotificationType = 'success' | 'error' | 'warning' | 'info' | 'loading';

export interface Notification {
    id: string;
    message: string;
    type: NotificationType;
    duration?: number;
}

interface NotificationToastProps {
    notification: Notification;
    onClose: (id: string) => void;
}

const ICONS: Record<NotificationType, React.ReactNode> = {
    success: <CheckCircle size={20} />,
    error: <XCircle size={20} />,
    warning: <AlertCircle size={20} />,
    info: <Info size={20} />,
    loading: <Loader size={20} className="notification-toast-spinner" />
};

export const NotificationToast: React.FC<NotificationToastProps> = ({ notification, onClose }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const startTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        // Loading não tem timeout automático
        if (notification.type === 'loading') {
            return;
        }

        const duration = notification.duration || 5000;
        timerRef.current = setTimeout(() => {
            if (!isHovered) {
                handleClose();
            }
        }, duration);
    };

    const stopTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const handleClose = () => {
        setIsExiting(true);
        setTimeout(() => {
            onClose(notification.id);
        }, 300);
    };

    useEffect(() => {
        startTimer();
        return () => stopTimer();
    }, []);

    useEffect(() => {
        if (isHovered) {
            stopTimer();
        } else {
            startTimer();
        }
    }, [isHovered]);

    return (
        <div
            className={`notification-toast notification-toast-${notification.type} ${isExiting ? 'notification-toast-exit' : ''}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={notification.type !== 'loading' ? handleClose : undefined}
        >
            <div className="notification-toast-icon">
                {ICONS[notification.type]}
            </div>
            <div className="notification-toast-message">
                {notification.message}
            </div>
            {notification.type !== 'loading' && (
                <button
                    className="notification-toast-close"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleClose();
                    }}
                >
                    <X size={16} />
                </button>
            )}
        </div>
    );
};
